import { createFileRoute } from "@tanstack/react-router";
import { InfoPage } from "@/components/InfoPage";

export const Route = createFileRoute("/terms-of-use")({
  head: () => ({
    meta: [
      { title: "Terms of Use | Picamini.online" },
      {
        name: "description",
        content:
          "Read the Terms of Use for Picamini.online, including acceptable use, intellectual property, third-party services, and service availability.",
      },
      { property: "og:site_name", content: "Picamini.online" },
      { property: "og:title", content: "Terms of Use | Picamini.online" },
      { property: "og:description", content: "Review the rules, responsibilities, limitations, and acceptable use terms for Picamini.online." },
      { property: "og:type", content: "article" },
      { property: "og:url", content: "https://picamini.online/terms-of-use" },
      { property: "og:image", content: "https://picamini.online/og-image.png" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Terms of Use | Picamini.online" },
      { name: "twitter:description", content: "The terms and responsibilities for using Picamini.online classroom and PDF tools." },
      { name: "twitter:image", content: "https://picamini.online/og-image.png" },
      { property: "og:locale", content: "en_US" },
      { name: "theme-color", content: "#4F46E5" },
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
     ],
    links: [
      { rel: "canonical", href: "https://picamini.online/terms-of-use" },
      { rel: "alternate", hrefLang: "en", href: "https://picamini.online/terms-of-use" },
      { rel: "alternate", hrefLang: "x-default", href: "https://picamini.online/terms-of-use" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://picamini.online/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Terms of Use",
              "item": "https://picamini.online/terms-of-use"
            }
          ]
        })
      }
    ]
  }),
  component: TermsOfUsePage,
});

function TermsOfUsePage() {
  return (
    <InfoPage
      eyebrow="Site policies"
      title="Terms of Use"
      description="These terms describe the conditions for using Picamini.online and its free classroom, decision-making, and PDF tools."
    >
      <p>
        <strong>Effective date: September 6, 2026</strong>
      </p>
      <h2>About these terms</h2>
      <p>
        In these terms, &quot;Picamini.online,&quot; &quot;we,&quot; &quot;our,&quot; and &quot;the site&quot; refer to the Picamini.online
        website and its tools. &quot;You&quot; refers to any person or organization accessing or using the
        site. These terms apply whether you visit from a computer, tablet, phone, or another device.
      </p>
      <h2>Acceptance of these terms</h2>
      <p>
        By accessing or using Picamini.online, you agree to these Terms of Use and our{" "}
        <a href="/privacy-policy">Privacy Policy</a>. If you do not agree with these terms, please
        do not use the site.
      </p>
      <h2>Permitted use</h2>
      <p>
        Picamini.online is provided for lawful personal, educational, classroom, professional, and
        informational use. You may use the tools for ordinary activities such as selecting names,
        organizing groups, managing classroom activities, and processing documents in your browser.
      </p>
      <p>You agree not to:</p>
      <ul>
        <li>Use the site for unlawful, abusive, fraudulent, or harmful activity</li>
        <li>Attempt to disrupt, overload, reverse engineer, or bypass the site&apos;s security</li>
        <li>Upload or enter content that you do not have the right to use</li>
        <li>Use automated methods to scrape, copy, or interfere with the service</li>
        <li>Rely on the site as a substitute for professional, legal, medical, or financial advice</li>
      </ul>
      <h2>User content and files</h2>
      <p>
        You retain responsibility for names, text, images, documents, and other content that you
        enter, upload, process, or export through a tool. You confirm that you have the necessary
        permission to use that content and that your use complies with applicable law and school or
        workplace policies. You should maintain your own backups of important files.
      </p>
      <h2>Tool output and responsibility</h2>
      <p>
        Some tools produce random, simulated, or user-configured results. Results are provided for
        convenience and entertainment and should be reviewed before being used in a consequential
        decision. You are responsible for checking documents, calculations, names, and other
        outputs before relying on them.
      </p>
      <h2>Intellectual property</h2>
      <p>
        The Picamini.online name, logo, website design, original text, and software are owned by
        Picamini.online or its licensors unless otherwise stated. You may use the site as intended,
        but you may not reproduce, resell, redistribute, or create a competing service from our
        original materials without permission.
      </p>
      <h2>Third-party services</h2>
      <p>
        The site may use third-party hosting, libraries, analytics, advertising, or external links.
        Third-party services are governed by their own terms and policies. We do not control and
        are not responsible for their availability, content, or practices.
      </p>
      <h2>Educational and organizational responsibility</h2>
      <p>
        Teachers, administrators, parents, and other responsible adults should use appropriate
        judgment when selecting tools for children or students. The site does not supervise users,
        verify classroom policies, or determine whether a particular activity is suitable for a
        specific age group, institution, or situation.
      </p>
      <h2>Availability and changes</h2>
      <p>
        We work to keep Picamini.online useful and available, but we do not guarantee uninterrupted,
        error-free, or permanently available access. We may change, suspend, or remove a tool or
        part of the site at any time. We may also update these terms when needed; continued use
        after an update means you accept the revised terms.
      </p>
      <h2>Disclaimer and limitation of liability</h2>
      <p>
        The site and its tools are provided on an &quot;as is&quot; and &quot;as available&quot; basis, without
        warranties of any kind to the fullest extent permitted by law. Picamini.online is not
        liable for indirect, incidental, special, consequential, or data-related loss arising from
        your use of the site. You should keep appropriate backups of important files and records.
      </p>
      <h2>Suspension or termination</h2>
      <p>
        We may limit, suspend, or end access to the site when reasonably necessary to protect the
        service, visitors, third-party rights, or our legal obligations. Provisions concerning
        intellectual property, responsibility, disclaimers, and limitations of liability remain
        effective after use of the site ends.
      </p>
      <h2>Contact</h2>
      <p>
        Questions about these terms can be sent to{" "}
        <a href="mailto:support@picamini.online">support@picamini.online</a>.
      </p>
    </InfoPage>
  );
}
